import {createStyles} from '@mantine/core';
import React, {ReactElement} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import navigateToURL from 'lib/navigateToURL';
import {
  ContainerRelevantItemsGraphQL,
  ContainerRelevantItemsGraphQL_RelevantItemsNode_tagged as TaggedItem,
  ContainerRelevantItemsGraphQL_RelevantItemsNode_filesMentioning as FilesMentioningItem,
  ContainerRelevantItemsGraphQL_RelevantItemsNode_processTasks_ProcessTaskNode as ProcessTaskItem,
  ContainerRelevantItemsGraphQL_RelevantItemsNode_processOutputs_ProcessOutputNode as ProcessOutputItem,
} from 'relevant-items/containers/__generated/ContainerRelevantItemsGraphQL';
import {HEADER_COLUMNS, HeaderInfo, NOT_SORTABLE} from 'relevant-items/RelevantItemsHeaderColumns';
import {
  RelevantRowData,
  matchName,
  matchModifiedAt,
  matchProjectFolder,
  matchReference,
  getSortedRelevantItemRowData,
  plainReferenceText,
} from 'relevant-items/shared/RelevantItemsTableUtils';
import BenchChip from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {Cell, CellProps, Column, Table} from 'ui-library/simple-table/SimpleTable';
import SortableHeaderCell from 'ui-library/simple-table/SortableHeaderCell';
import useSimpleTableSortState from 'ui-library/simple-table/useSimpleTableSortState';
import APIUtil from 'util/APIUtil';
import * as DateUtil from 'util/DateUtil';
import filterItemsToSearchQuery from 'util/filterItemsToSearchQuery';

import {
  ContainerRelevantItemsFilteredTableState,
  ContainerRelevantItemsFilterOption,
  UpdateContainerRelevantItemsFilteredTableState,
} from './ContainerRelevantItemsFilteredTableState';

type TaggedStorableItem = ContainerRelevantItemsGraphQL['taggedStorables'][number];

const useStyles = createStyles((theme) => ({
  grayHoverHeaderCell: {
    transition: 'background-color 0.1s',
    backgroundColor: theme.colors.gray[0],
    '&:hover': {
      backgroundColor: theme.colors.gray[2],
    },
  },
}));

const CONTAINER_LABEL = 'Container';

function getProjectFolderName(item: {
  id: string;
  folder?: {source?: {name: string}} | null;
  parent?: {folder?: {source?: {name: string}} | null; __typename?: string} | null;
  source?: {name: string} | null;
}): string | null {
  return item.folder?.source?.name ?? item.parent?.folder?.source?.name ?? item.source?.name ?? null;
}

type SupportedRelevantItem<T> = Exclude<T, {__typename: 'EntryLikeDocumentNode'}>;

function isWorksheetStep(item: {__typename?: string; parentId?: string | null}): boolean {
  return (
    item.__typename === 'EntryLikeDocumentNode' && item.parentId != null && APIUtil.isWorksheet(item.parentId)
  );
}

type ContainerRelevantProcess =
  | ContainerRelevantItemsGraphQL['processTasks'][number]
  | ContainerRelevantItemsGraphQL['processOutputs'][number];
/**
 * If ELN_ENABLE_DOCUMENT_NOTE_REFERENCES is false, then worksheet steps will not be included in the table.
 */
function getSupportedContainerRelevantItems(
  tagged: Array<TaggedItem>,
  filesMentioning: Array<FilesMentioningItem>
): {
  supportedTagged: Array<TaggedItem>;
  supportedFilesMentioning: Array<FilesMentioningItem>;
} {
  if (BenchlingConfig.ELN_ENABLE_DOCUMENT_NOTE_REFERENCES) {
    return {supportedTagged: tagged, supportedFilesMentioning: filesMentioning};
  }
  return {
    supportedTagged: tagged.filter(
      (item): item is SupportedRelevantItem<TaggedItem> => item.__typename !== 'EntryLikeDocumentNode'
    ),
    supportedFilesMentioning: filesMentioning.filter(
      (item): item is SupportedRelevantItem<FilesMentioningItem> =>
        item.__typename !== 'EntryLikeDocumentNode'
    ),
  };
}

function getWorksheetStepMentionRow(item: FilesMentioningItem): RelevantRowData | null {
  if (!isWorksheetStep(item)) {
    return null;
  }
  return buildContainerBenchChipRow(item, `${CONTAINER_LABEL} referenced in step '${item.name}'`);
}
function getEntryMentionRow(item: FilesMentioningItem): RelevantRowData | null {
  if (!APIUtil.isEntry(item.id)) {
    return null;
  }
  return buildContainerBenchChipRow(item, `${CONTAINER_LABEL} referenced in entry`);
}
function getBiologicalEntityDescriptionMentionRow(item: FilesMentioningItem): RelevantRowData | null {
  if (!APIUtil.isBiologicalEntity(item.id)) {
    return null;
  }
  return buildContainerBenchChipRow(item, `${CONTAINER_LABEL} referenced in description`);
}

function getAnyDescriptionMentionRow(item: FilesMentioningItem): RelevantRowData {
  return buildContainerBenchChipRow(item, `${CONTAINER_LABEL} referenced in description`);
}

function buildContainerBenchChipRow(
  item: TaggedItem | FilesMentioningItem | TaggedStorableItem,
  referencedInText: string
): RelevantRowData {
  return {
    id: item.id,
    name: item.name,
    referencedInText: plainReferenceText(referencedInText),
    modifiedAt: item.modifiedAt,
    location: getProjectFolderName(item),
    chip: <BenchChip id={item.id} size={ChipSize.SMALL} />,
  };
}

function getEntityLinkRows(taggedItemsToInclude: Array<TaggedItem>): Array<RelevantRowData> {
  return taggedItemsToInclude.map((item) =>
    buildContainerBenchChipRow(item, `${CONTAINER_LABEL} referenced in field`)
  );
}

/**
 * Sorts the files mentioning items to their respective types and returns an array of the data for the table.
 */
function getFilesMentioningRowForItem(
  item: FilesMentioningItem,
  itemsToShow: ContainerRelevantItemsFilterOption
): RelevantRowData | null {
  switch (itemsToShow) {
    case ContainerRelevantItemsFilterOption.ALL:
      return (
        getWorksheetStepMentionRow(item) ?? getEntryMentionRow(item) ?? getAnyDescriptionMentionRow(item)
      );
    case ContainerRelevantItemsFilterOption.WORKSHEET_STEPS:
      return getWorksheetStepMentionRow(item);
    case ContainerRelevantItemsFilterOption.ENTRIES:
      return getEntryMentionRow(item);
    case ContainerRelevantItemsFilterOption.ENTITIES:
      return getBiologicalEntityDescriptionMentionRow(item);
    case ContainerRelevantItemsFilterOption.PROCESSES:
    case ContainerRelevantItemsFilterOption.INVENTORY:
      return null;
    default:
      throw new Error('Invalid relevant items filter option');
  }
}

function getFilesMentioningRows(
  supportedFilesMentioning: Array<FilesMentioningItem>,
  itemsToShow: ContainerRelevantItemsFilterOption
): Array<RelevantRowData> {
  return supportedFilesMentioning.flatMap((item) => {
    const row = getFilesMentioningRowForItem(item, itemsToShow);
    return row != null ? [row] : [];
  });
}

function getStorableLinkRows(taggedStorables: Array<TaggedStorableItem>): Array<RelevantRowData> {
  return taggedStorables.map((item) =>
    buildContainerBenchChipRow(item, `${CONTAINER_LABEL} referenced in field`)
  );
}

function getInventoryTaggedRowsForFilter(
  taggedStorables: Array<TaggedStorableItem>,
  itemsToShow: ContainerRelevantItemsFilterOption
): Array<RelevantRowData> {
  switch (itemsToShow) {
    case ContainerRelevantItemsFilterOption.ALL:
    case ContainerRelevantItemsFilterOption.INVENTORY:
      return getStorableLinkRows(taggedStorables);
    default:
      return [];
  }
}

function getTaskGroupsRows(
  processOutputs: Array<ProcessOutputItem>,
  processTasks: Array<ProcessTaskItem>
): Array<RelevantRowData> {
  return [...processOutputs, ...processTasks].map((item: ContainerRelevantProcess): RelevantRowData => {
    const {name, process} = item;
    const {modifiedAt, createdAt, editUrl, folder} = process;
    return {
      name,
      id: name,
      referencedInText: plainReferenceText(`${CONTAINER_LABEL} referenced in task group`),
      modifiedAt: modifiedAt ?? createdAt,
      location: folder?.name ?? null,
      chip: (
        <Chip
          text={name}
          icon="process"
          size={ChipSize.SMALL}
          className="chip-container"
          onClick={() => {
            navigateToURL(editUrl ?? '');
            analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_RELEVANT_ITEM, {
              process_client_origin: item.process.processTaskSchema.clientOrigin,
            });
          }}
        />
      ),
    };
  });
}

function getTaggedRowsForFilter(
  supportedTagged: Array<TaggedItem>,
  itemsToShow: ContainerRelevantItemsFilterOption
): Array<RelevantRowData> {
  switch (itemsToShow) {
    case ContainerRelevantItemsFilterOption.ALL:
      return getEntityLinkRows(supportedTagged);
    case ContainerRelevantItemsFilterOption.ENTITIES:
      return getEntityLinkRows(supportedTagged.filter((item) => APIUtil.isBiologicalEntity(item.id)));
    case ContainerRelevantItemsFilterOption.ENTRIES:
      return getEntityLinkRows(supportedTagged.filter((item) => APIUtil.isEntry(item.id)));
    default:
      return [];
  }
}

function getTaskGroupsRowsForFilter(
  processOutputs: Array<ProcessOutputItem>,
  processTasks: Array<ProcessTaskItem>,
  itemsToShow: ContainerRelevantItemsFilterOption
): Array<RelevantRowData> {
  if (
    itemsToShow !== ContainerRelevantItemsFilterOption.ALL &&
    itemsToShow !== ContainerRelevantItemsFilterOption.PROCESSES
  ) {
    return [];
  }
  return getTaskGroupsRows(processOutputs, processTasks);
}

/**
 * Creates table rows for a container's relevant items.
 *
 * Takes in the active filter option and transforms the GraphQL relevant items data and returns an array of
 * the data for the tab. If ELN_ENABLE_DOCUMENT_NOTE_REFERENCES is false, then worksheet steps will not
 * be included in the table.
 */
function getContainerRelevantItemRowData(
  itemsToShow: ContainerRelevantItemsFilterOption,
  relevantItemsGraphQL: ContainerRelevantItemsGraphQL
): Array<RelevantRowData> {
  const {filesMentioning, processOutputs, processTasks, tagged, taggedStorables} = relevantItemsGraphQL;
  const {supportedTagged, supportedFilesMentioning} = getSupportedContainerRelevantItems(
    tagged,
    filesMentioning
  );
  return [
    ...getTaggedRowsForFilter(supportedTagged, itemsToShow),
    ...getInventoryTaggedRowsForFilter(taggedStorables, itemsToShow),
    ...getFilesMentioningRows(supportedFilesMentioning, itemsToShow),
    ...getTaskGroupsRowsForFilter(processOutputs, processTasks, itemsToShow),
  ];
}

export interface ContainerRelevantItemsSectionTableProps {
  relevantItemsGraphQL: ContainerRelevantItemsGraphQL;
  filteredTableState: ContainerRelevantItemsFilteredTableState;
  updateFilteredTableState: UpdateContainerRelevantItemsFilteredTableState;
  containerId: string;
  /** @default false */
  isCompact?: boolean;
  /** @default 100 */
  paginationLimit?: number;
}

// Table part of the container relevant items section.
export default function ContainerRelevantItemsSectionTable({
  relevantItemsGraphQL,
  filteredTableState,
  updateFilteredTableState,
  containerId,
  isCompact = false,
  paginationLimit = 100,
}: ContainerRelevantItemsSectionTableProps): JSX.Element {
  const {classes} = useStyles();
  const {pageOffset, searchQuery, filterOption} = filteredTableState;

  const allRowData = getContainerRelevantItemRowData(filterOption, relevantItemsGraphQL);
  const filteredRowData = filterItemsToSearchQuery(allRowData, searchQuery, [
    matchName,
    matchModifiedAt,
    matchProjectFolder,
    matchReference,
  ]);
  const {sortState, getHeaderCellProps} = useSimpleTableSortState(containerId);
  const rowData = getSortedRelevantItemRowData(
    filteredRowData,
    sortState ?? {
      columnKey: HEADER_COLUMNS.modifiedAt.sortIndex,
      sortDirection: 'arrow-up',
    }
  );
  const maxPageOffset = Math.max(0, rowData.length - paginationLimit);
  const clampedPageOffset = Math.min(pageOffset, maxPageOffset);
  const numRows = Math.min(rowData.length - clampedPageOffset, paginationLimit);

  function renderHeader(headerInfo: HeaderInfo): ReactElement<CellProps> {
    assert(headerInfo.sortIndex !== NOT_SORTABLE, 'Expected header to have sortable index');

    return (
      <SortableHeaderCell {...getHeaderCellProps(headerInfo.sortIndex)}>{headerInfo.name}</SortableHeaderCell>
    );
  }

  function setOffset(nextPageOffset: number): void {
    updateFilteredTableState({pageOffset: nextPageOffset});
  }

  return (
    <Table
      emptyTableText="No relevant items were found."
      numRows={numRows}
      pagination={{offset: clampedPageOffset, limit: paginationLimit, total: rowData.length}}
      setOffset={setOffset}
      isCompact={isCompact}
    >
      <Column
        header={renderHeader(HEADER_COLUMNS.item)}
        renderCell={(rowIndex) => <Cell>{rowData[clampedPageOffset + rowIndex].chip}</Cell>}
      />
      <Column
        header={<Cell className={classes.grayHoverHeaderCell}>{HEADER_COLUMNS.reference.name}</Cell>}
        renderCell={(rowIndex) => <Cell>{rowData[clampedPageOffset + rowIndex].referencedInText}</Cell>}
      />
      <Column
        header={renderHeader(HEADER_COLUMNS.modifiedAt)}
        renderCell={(rowIndex) => (
          <Cell>
            {rowData[clampedPageOffset + rowIndex].modifiedAt &&
              DateUtil.readableTime(rowData[clampedPageOffset + rowIndex].modifiedAt)}
          </Cell>
        )}
      />
      <Column
        header={renderHeader(HEADER_COLUMNS.projectLocation)}
        renderCell={(rowIndex) => <Cell>{rowData[clampedPageOffset + rowIndex].location}</Cell>}
      />
    </Table>
  );
}
